<template>
  <div class="page-page tier tier-three">
    <Notification
      v-if="notification.active"
      :content="notification.content"
      :type="notification.type"
    />

    <div class="tier-content">
      <Tooltip
        content="Here you can preview and edit page content, along with various settings"
      />

      <Tabs ref="pageTabs" :headings="['Page Widgets', 'Page Settings']">
        <Tab>
          <div>
            <div class="centered no-widgets" v-if="!hasList">
              <p>
                <strong class="name">{{ page.name }}</strong> doesn't have any
                widgets yet. Don't worry, it's super easy to add them! Start by
                using the <strong>Add Content</strong> button.
              </p>

              <router-link class="btn is-primary" :to="{name: 'add-list'}"
                >Add content</router-link
              >
            </div>

            <section class="list-table" v-if="hasList">
              <table-data
                :rows="lists"
                @reorderItems="reorderItems"
                :sortable="true"
                :headings="[
                  {heading: '', key: 'edit'},
                  {heading: '', key: 'actions'}
                ]"
              >
                <div
                  v-for="(list, index) in lists"
                  :slot="'td-' + index + '-edit'"
                  :key="list.id"
                >
                  <router-link
                    class="list-link"
                    :to="listItemLink(list, listTypeAllowMany(list))"
                  >
                    <div>
                      <Sprite :name="`list-${listType(list)}`" />
                    </div>
                    <div class="list-type">
                      <p>{{ list.name }}</p>
                      <p class="type">{{ listType(list) | humanize }}</p>
                    </div>
                  </router-link>
                </div>

                <div
                  class="select-action action"
                  v-for="(list, index) in lists"
                  :slot="'td-' + index + '-actions'"
                  :key="`list-options-${list.id}`"
                >
                  <select class="is-dark" @change="triggerAction(list, $event)">
                    <option>Choose Action</option>
                    <option value="editItem">Edit</option>
                    <option value="activateDuplicateList">Duplicate</option>
                    <option value="deleteList">Delete</option>
                  </select>
                </div>
              </table-data>

              <router-link :to="{name: 'add-list'}" class="add-page-content">
                Add content
              </router-link>
            </section>
          </div>
        </Tab>

        <Tab>
          <div>
            <div class="site-page">
              <edit-page-form
                v-model="page"
                :isHomePage="isHomePage"
                :websiteUrl="site.domain"
                @submit="submitPage"
              />
            </div>

            <hr />

            <h2>Actions</h2>
            <div class="page-actions">
              <Btn v-if="!isHomePage" tertiary @click="setAsHomePage">
                Set as home page
              </Btn>

              <Btn subtle @click="activateDeleteConfirm">
                Delete Page
              </Btn>
            </div>
          </div>
        </Tab>
      </Tabs>

      <router-view :pageName="page.name" @updatePage="updatePage" />

      <Confirm
        v-if="deleteConfirmActive"
        @confirm="deletePage"
        @cancel="cancelDelete"
        :message="deleteMessage"
        :link="{
          name: 'page',
          params: {
            companyId: this.companyId,
            siteId: this.siteId,
            pageId: this.pageId
          }
        }"
      />

      <Overlay v-if="isDuplicatingList" width="x-small" autoHeight>
        <duplicate-list-form
          :pages="sortedPages"
          :list="list"
          :isLoading="duplicateLoading"
          v-model="duplicatingList"
          @submit="duplicateList"
        >
        </duplicate-list-form>
      </Overlay>
    </div>
  </div>
</template>

<script>
import page from '@/mixins/page'
import confirm from '@/mixins/confirm'
import notification from '@/mixins/notification'

import {readCompanySiteData} from '@/lib/sites'

import {
  readCompanySitePageData,
  readCompanySitePagesData,
  deleteCompanySitePageData,
  updateCompanySitePageData
} from '@/lib/pages'

import {
  readCompanySitePageListsData,
  createCompanySitePageListData,
  updateCompanySitePageListData,
  deleteCompanySitePageListData
} from '@/lib/lists'

import {
  createCompanySitePageListListItemData,
  updateCompanySitePageListListItemData,
  readCompanySitePageListListItemsData
} from '@/lib/list_items'

import {readListTypesData} from '@/lib/list_types'

import EditPageForm from '@/components/forms/EditPageForm'
import DuplicateListForm from '@/components/forms/DuplicateListForm'
import Table from '@/components/Table'
import Tabs from '@/components/Tabs'
import Tab from '@/components/Tab'
import Btn from '@/components/Btn'
import Tooltip from '@/components/Tooltip'
import Sprite from '@/components/Sprite'
import Overlay from '@/components/Overlay'

import debounce from 'lodash.debounce'

export default {
  name: 'Page',

  props: {
    companyId: {
      type: String,
      required: true
    },

    siteId: {
      type: String,
      required: true
    },

    pageId: {
      type: String,
      required: true
    }
  },

  mixins: [page, confirm, notification],

  components: {
    'table-data': Table,
    Btn,
    Tooltip,
    EditPageForm,
    DuplicateListForm,
    Sprite,
    Overlay,
    Tabs,
    Tab
  },

  data() {
    return {
      site: {},
      page: {},
      storedPagePath: '',
      homePageChanged: {},
      duplicatedList: [],
      isDuplicatingList: false,
      duplicatingList: [],
      activeListItems: [],
      firstListListItems: [],
      updatedHome: {},
      pages: [],
      list: {},
      pageCount: 5,
      deleteConfirmActive: false,
      duplicateLoading: false,
      lists: [],
      listTypes: [],
      errors: {}
    }
  },

  methods: {
    getData() {
      readCompanySiteData(this.companyId, this.siteId).then(({data}) => {
        this.site = data
      })

      readCompanySitePagesData(this.companyId, this.siteId, {
        page: 1,
        page_size: 250
      }).then(({data}) => {
        this.pages = data
      })

      readCompanySitePageData(this.companyId, this.siteId, this.pageId).then(
        ({data}) => {
          let page = data
          this.page = page
          this.storedPagePath = page.path
        }
      )

      readListTypesData().then(({data}) => {
        this.listTypes = data
      })

      readCompanySitePageListsData(this.companyId, this.siteId, this.pageId, {
        page: 1,
        page_size: 250
      }).then(({data, page}) => {
        let lists = data

        this.firstListListItems = []

        lists.forEach((list) => {
          // Get the first list item in each list
          readCompanySitePageListListItemsData(
            this.companyId,
            this.siteId,
            this.pageId,
            list.id,
            {page: 1, page_size: 1}
          ).then(({data}) => {
            if (data[0]) {
              data[0].belongsTo = list.id
              this.firstListListItems.push(data[0])
            }
          })
        })

        this.lists = data
        this.pageData = page
      })
    },

    setAsHomePage() {
      // Check if there is already a home page
      let currentHomePage = this.pages.find((page) => page.path === '/')

      if (currentHomePage) {
        let currentPageName = currentHomePage.name.replace(/\s+/g, '-').toLowerCase()
        let currentHomePathNew = {path: `/${currentPageName}`}

        this.renameCurrentHomePage(currentHomePage, currentHomePathNew)
      } else {
        this.changePathToHome()
      }
    },

    renameCurrentHomePage(currentHomePage, currentHomePathNew) {
      updateCompanySitePageData(
        this.companyId,
        this.siteId,
        currentHomePage.id,
        currentHomePathNew
      ).then(({data}) => {
        this.changePathToHome()
        this.updatedHome = data
      })
    },

    changePathToHome() {
      let data = {path: '/'}

      updateCompanySitePageData(
        this.companyId,
        this.siteId,
        this.pageId,
        data
      ).then(({data}) => {
        this.getData()
        this.$emit('updatePage')
        this.triggerNotification('success', 'site home page', 'changed')
      })
    },

    cancelDelete() {
      this.deleteConfirmActive = false
    },

    triggerAction(listItem, e) {
      let value = e.target.value
      this[value](listItem)
    },

    editItem(list) {
      let allowMany = this.listTypeAllowMany(list)
      this.$router.push(this.listItemLink(list, allowMany))
    },

    listType(list) {
      let listTypeId = list.list_type_id

      let name = this.listTypes.find((list) => {
        return listTypeId === list.id
      }).name

      return name
    },

    listTypeAllowMany(list) {
      let listTypeId = list.list_type_id

      let allowMany = this.listTypes.find((list) => {
        return listTypeId === list.id
      }).allow_many_items

      return allowMany
    },

    submitPage() {
      let path = this.page.path
      let pageHasSlash = path.charAt(0) === '/'

      if (!pageHasSlash) {
        path = '/' + path
      }

      updateCompanySitePageData(
        this.companyId,
        this.siteId,
        this.pageId,
        this.page
      ).then(({data}) => {
        this.$emit('updatePage')
        this.triggerNotification('success', 'page', 'updated')
      })
    },

    deletePage() {
      deleteCompanySitePageData(this.companyId, this.siteId, this.pageId).then(
        () => {
          this.deleteConfirmActive = false
          this.$emit('updatePage')
          this.triggerNotification('success', 'page', 'deleted')

          window.setTimeout(() => {
            this.$router.push({
              name: 'pages',
              params: {companyId: this.companyId, siteId: this.siteId}
            })
          }, this.closeDelay)
        }
      )
    },

    deleteList(list) {
      deleteCompanySitePageListData(
        this.companyId,
        this.siteId,
        this.pageId,
        list.id
      ).then(() => {
        this.getData()
        this.triggerNotification('success', 'page section', 'deleted')
        this.$emit('updatePage')
      })
    },

    activateDuplicateList(list) {
      this.isDuplicatingList = true
      this.duplicatingList = list

      this.duplicatedList.weight = list.weight + 1

      this.duplicatingList = {
        name: `${this.duplicatingList.name} (copied)`,
        list_type_id: this.duplicatingList.list_type_id,
        weight: this.duplicatingList.weight
      }

      readCompanySitePageListListItemsData(
        this.companyId,
        this.siteId,
        this.pageId,
        list.id,
        {page: 1, page_size: 200}
      ).then(({data}) => {
        this.activeListItems = data
      })
    },

    duplicateList() {
      this.duplicateLoading = true

      createCompanySitePageListData(
        this.companyId,
        this.siteId,
        this.duplicatingList.pageId,
        this.duplicatingList
      ).then(({data}) => {
        let createdList = data
        let hasListItems = this.activeListItems.length > 0

        if (hasListItems) {
          this.activeListItems.forEach((item) => {
            let hasImage = item.image_versions.length > 0

            createCompanySitePageListListItemData(
              this.companyId,
              this.siteId,
              this.duplicatingList.pageId,
              createdList.id,
              item
            )
              .then(({data}) => {
                let createdListItem = data

                if (hasImage) {
                  let image = item.image_versions[0].url
                  let img = {image: image}
                  let pageId = this.duplicatingList.pageId

                  updateCompanySitePageListListItemData(
                    this.companyId,
                    this.siteId,
                    pageId,
                    createdList.id,
                    createdListItem.id,
                    img
                  )
                }
              })
              .finally(() => {
                this.duplicateLoading = false
                this.isDuplicatingList = false

                this.triggerNotification(
                  'success',
                  'page section',
                  'duplicated'
                )
              })
              .catch(({response}) => {
                this.errors = response.errors
              })
          })
        } else {
          this.triggerNotification('success', 'page section', 'duplicated')
        }

        // this.duplicatingList = []
        this.getData()
      })
    },

    updateListWeights(items) {
      items.forEach((item, index) => {
        item.weight = index

        updateCompanySitePageListData(
          this.companyId,
          this.siteId,
          this.pageId,
          item.id,
          item
        )
          .then(({data}) => {
            this.$emit('updatePage')
          })
          .catch(({response}) => {
            this.errors = response.errors
          })
      })
    },

    updatePage() {
      this.$emit('updatePage')
    },

    activateDeleteConfirm() {
      this.deleteConfirmActive = true
    }
  },

  computed: {
    title() {
      return `${this.page.name} Page`
    },

    isHomePage() {
      return this.storedPagePath === '/'
    },

    hasList() {
      return !!this.lists.length
    },

    sortedPages() {
      let pages = this.pages

      if (pages.indexOf(this.page) > 0) {
        pages.splice(pages.indexOf('role'), 1)
        pages.unshift('role')
      }

      return pages
    },

    listItemLink() {
      return (list, allowMany) => {
        let firstItem = this.firstListListItems.find(
          (item) => item.belongsTo === list.id
        )

        let hasFirstListItem = !!firstItem

        if (!allowMany && hasFirstListItem) {
          return {
            name: 'EditListItem',
            params: {
              companyId: this.companyId,
              siteId: this.siteId,
              pageId: this.pageId,
              listId: list.id,
              listItemId: firstItem.id
            }
          }
        } else {
          return {
            name: 'list',
            params: {
              companyId: this.companyId,
              siteId: this.siteId,
              pageId: this.pageId,
              listId: list.id
            }
          }
        }
      }
    },

    deleteMessage() {
      return `Are you sure you wish to delete the page, <strong class="name">${this.page.name}</strong>?`
    }
  },

  filters: {
    humanize(value) {
      return value.replace(/-/g, ' ')
    }
  },

  mounted() {
    this.getData()
  },

  created() {
    this.reorderItems = debounce(
      (items) => {
        this.updateListWeights(items)
      },
      333,
      {trailing: true}
    )
  },

  watch: {
    $route() {
      this.getData()
    },

    lists() {
      /**
       * When a list is added, change the height of the tabs container
       */
      this.$refs.pageTabs.initTab()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-page
  .site-page-content
    background: #fff
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.12)
    border-radius: 5px
    padding-bottom: $gap-x-small

    .option-heading
      font-size: 14px
      margin-top: $gap-small

  .page-actions
    display: flex

    .btn
      margin-right: $gap-small

  .add-page-content
    padding: $gap-medium
    border-top: 1px solid $color-border-light
    width: 100%
    text-align: center
    display: block
    text-transform: uppercase

    @include themify($themes)
      color: themed('color-secondary')

  .table-heading
    background: #f3f3f3
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.15)
    padding: $gap-x-small $gap-small
    border-radius: 5px

    h2
      border: none
      margin-bottom: 0

    .heading-button
      top: 50%
      transform: translateY(-50%)
      right: $gap-small
      padding: 10px

  .list-table
    margin-bottom: 0

    .inner-scroll
      max-height: none

    .list-link
      display: flex
      align-items: center

      .sprite
        width: 20px
        height: 20px
        margin-right: $gap-small

    .sprite
      @include themify($themes)
        fill: themed('color-secondary')

  .list-type
    p
      margin: 0 0 0 $gap-x-small
      line-height: 1.4

      &.type
        font-size: 12px
        opacity: 0.3
        text-transform: capitalize

.table-heading
  display: flex
  align-items: center
  justify-content: space-between
  position: relative

  .btn
    padding: 3px 5px

.image-preview
  max-width: 300px

.no-widgets
  padding: $gap-small

  .btn
    margin: $gap-small 0
    animation: pulse 1s infinite ease-in-out
</style>
